import * as preact from "preact";

import * as onboarding from "onboarding";
import * as languageMap from "shared/languageMap";
import stores from "stores/stores";
import LanguageSelectorView from "views/LanguageSelectorView";
import { SignupComponent } from "views/SignupView";
import * as walkthrough from "walkthrough";

interface Props {
  appRouter: any;
  options?: {
    learningLanguage?: string;
  };
}

interface State {
  step: "selectLearningLanguage" | "selectFirstLanguage" | "signup";
}

export default class StartPageView extends preact.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const learningLanguage = props.options?.learningLanguage;

    if (learningLanguage && languageMap.map[learningLanguage]) {
      stores.user
        .save({
          currentLearningLanguage: learningLanguage,
        })
        .then(() => {
          walkthrough.setAchievement("setLanguages", true);
          this.forceUpdate();
        });

      if (learningLanguage === "en") {
        // If learning English, ask for first language
        this.state = {
          step: "selectFirstLanguage",
        };
      } else {
        this.state = {
          step: "signup",
        };
      }
    } else if (walkthrough.hasAchievement("setLanguages")) {
      this.state = {
        step: "signup",
      };
    } else {
      this.state = {
        step: "selectLearningLanguage",
      };
    }

    this.redirectIfAppropriate();
  }

  async redirectIfAppropriate() {
    // Check if user has already signed in
    if (!(await stores.user.isLocalMode.value)) {
      // If signed in, redirect to destination
      this.redirectToDestination();
      return;
    }
  }

  handleLearningLanguageSelected = async (languageCode: string) => {
    await stores.user.save({
      currentLearningLanguage: languageCode,
    });

    if (languageCode === "en") {
      // If learning English, ask for first language
      this.setState({ step: "selectFirstLanguage" });
    } else {
      // Otherwise assume first language is English
      this.setState({
        step: "signup",
      });
      walkthrough.setAchievement("setLanguages", true);
    }
  };

  handleFirstLanguageSelected = async (languageCode: string) => {
    this.setState({
      step: "signup",
    });

    await stores.user.save({
      firstLanguage: languageCode,
    });
    walkthrough.setAchievement("setLanguages", true);
  };

  handleSignupCancel = () => {
    localStorage.setItem("shownSignup", "true");
    this.redirectToDestination();
  };

  redirectToDestination = () => {
    const urlParams = new URLSearchParams(window.location.search);
    let redirectPath = urlParams.get("redirectPath");

    // Replace language-specific library path with generic /library path
    if (/\/[a-z]{2,3}\/library/.test(redirectPath)) {
      redirectPath = "/library";
    }
    this.props.appRouter.navigate(redirectPath ?? onboarding.firstPagePath(), {
      trigger: true,
    });
  };

  renderLanguageDialog = (
    title: string,
    languageType: "learning" | "first",
    onLanguageSelected: (code: string) => void
  ) => {
    const flagLanguages = languageMap.flagList.slice(0, 12).map((language) => {
      const languageCode =
        typeof language === "string" ? language : language.code;
      const languageData =
        typeof language === "string" ? languageMap.map[language] : language;
      return {
        ...languageData,
        code: languageCode,
      };
    });

    return (
      <div class="startPageDialog" id="languageSelectDialog">
        <div class="languageDialogHeading">
          <img
            class="languageRabbit"
            src="/content/landingPages/rabbit-language-thought.webp"
            alt=""
            width="1224"
            height="1285"
          />
          <h2>{title}</h2>
        </div>

        <ul class="flags">
          {flagLanguages.map((language) => (
            <li class="hasTooltip" key={language.code}>
              <div
                class="clickable"
                onClick={() => onLanguageSelected(language.code)}
              >
                <img src={`/external/flags/${language.code}.svg`} />
                <div class="language-label">{language.english}</div>
              </div>
            </li>
          ))}
        </ul>

        <div id="languageSelectorView">
          <LanguageSelectorView
            languageType={languageType}
            searchOnlyMode={true}
            setLanguage={onLanguageSelected}
          />
        </div>
      </div>
    );
  };

  renderSignupView = () => {
    return (
      <div class="startPageDialog startPageSignupDialog">
        <div class="signupHeading">
          <h2>Let's get started</h2>
        </div>
        <button class="closeButton" onClick={this.handleSignupCancel}>
          <i class="icon-remove" />
        </button>
        <div class="signupContainer">
          <SignupComponent
            stores={stores}
            benefitsIllustration={
              <img
                class="welcomeRabbit"
                src="/content/landingPages/rabbit-peeking-book.webp"
                alt=""
                width="1254"
                height="1254"
              />
            }
          />
        </div>
      </div>
    );
  };

  render() {
    return (
      <>
        {this.state.step === "selectLearningLanguage" &&
          this.renderLanguageDialog(
            "What language do you want to learn?",
            "learning",
            this.handleLearningLanguageSelected
          )}

        {this.state.step === "selectFirstLanguage" &&
          this.renderLanguageDialog(
            "What is your first language?",
            "first",
            this.handleFirstLanguageSelected
          )}

        {this.state.step === "signup" && this.renderSignupView()}
      </>
    );
  }
}
